import {
  Form,
  Button,
  Dropdown,
  Modal,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import {
  EditIcon,
  FileListIcon,
  FilterTwoIcon,
  TrashIcon,
  ExportIcon,
  AddCircleIcon,
} from "../Icons";
import TableComponent from "../TableComponent";
import DotsHorizontalIcon from "mdi-react/DotsHorizontalIcon";
import EyeIcon from "mdi-react/EyeIcon";
import CachedIcon from "mdi-react/CachedIcon";
import {
  FaMapPin,
  FaWrench,
  FaTruck,
  FaGasPump,
  FaCalendar,
  FaMapMarkerAlt,
  FaStickyNote,
  FaDollarSign,
  FaClock,
} from "react-icons/fa";
import ConfirmDialog from "../ConfirmDialogue";
import { useAuth } from "../../hooks/useAuth";
import queryString from "query-string";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useDebounce, { useQueryParams } from "../../utils/hooks";
// import { Modal, Button } from "react-bootstrap";
import {} from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import ModalLoader from "../utils/ModalLoader";
import VehicleDetailsModal from "./VehicleDetailsModal";

export default function FleetVehicleManagement() {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);

  const initialFilterParams = {
    q: "",
    status: "",
    fuelType: "",
    isActive: "",
    startDate: "",
    endDate: "",
    enableDateRange: false,
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 20,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
  }, [debouncedFilterParams]);

  const [newVehicle, setNewVehicle] = useState({
    fleetNumber: "",
    warehouse: "",
    licensePlate: "",
    vin: "",
    make: "",
    model: "",
    year: new Date().getFullYear(),
    color: "",
    fuelType: "diesel",
    fuelCapacity: 0,
    odometer: 0,
    engineHours: 0,
    capacityWeight: 0,
    capacityVolume: 0,
    purchaseDate: "",
    purchasePrice: 0,
    gpsDeviceId: "",
    status: "available",
    isActive: true,
    notes: "",
  });

  const statusOptions = [
    { value: "available", label: "Available" },
    { value: "in_use", label: "In Use" },
    { value: "maintenance", label: "Maintenance" },
    { value: "out_of_service", label: "Out of Service" },
    { value: "retired", label: "Retired" },
  ];

  const fuelTypeOptions = [
    "gasoline",
    "diesel",
    "electric",
    "hybrid",
    "cng",
    "lpg",
  ];

  // Fetch vehicles
  const fetchVehicles = async (params) => {
    const response = await fetch(
      `${backendUrl}/api/fleet/vehicles?${queryString.stringify(params)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data;
  };

  const {
    data = { vehicles: [], count: 0, page: 1, limit: 20 },
    refetch,
    isLoading,
  } = useQuery(
    ["FLEET_VEHICLES", queryParams],
    () => fetchVehicles(queryParams),
    {
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  // Fetch vehicle details with related data
  const fetchVehicleDetails = async (vehicleId) => {
    try {
      setIsMainLoading(true);
      const response = await fetch(
        `${backendUrl}/api/fleet/vehicles/${vehicleId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.log(error);
    } finally {
      setIsMainLoading(false);
    }
  };

  const { data: vehicleDetails, refetch: refetchDetails } = useQuery(
    ["VEHICLE_DETAILS", selectedVehicle?.vehicle_id],
    () => fetchVehicleDetails(selectedVehicle.vehicle_id),
    {
      enabled: !!selectedVehicle && showDetailsModal,
      staleTime: 0,
    }
  );

  // Create vehicle mutation
  const createVehicleMutation = useMutation(
    async (vehicleData) => {
      const response = await fetch(`${backendUrl}/api/fleet/vehicles`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(vehicleData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Vehicle created successfully");
        queryClient.invalidateQueries(["FLEET_VEHICLES"]);
        setShowAddModal(false);
        resetNewVehicle();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Update vehicle mutation
  const updateVehicleMutation = useMutation(
    async ({ vehicleId, vehicleData }) => {
      const response = await fetch(
        `${backendUrl}/api/fleet/vehicles/${vehicleId}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(vehicleData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Vehicle updated successfully");
        queryClient.invalidateQueries(["FLEET_VEHICLES"]);
        setShowEditModal(false);
        setEditVehicle(null);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Delete vehicle mutation
  const deleteVehicleMutation = useMutation(
    async (vehicleId) => {
      const response = await fetch(
        `${backendUrl}/api/fleet/vehicle/${vehicleId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Vehicle deleted successfully");
        queryClient.invalidateQueries(["FLEET_VEHICLES"]);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilterParams({
      ...filterParams,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetFilters = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSearch = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetailsModal(true);
  };

  const handleEdit = (vehicle) => {
    setEditVehicle(vehicle);
    setShowEditModal(true);
  };

  const handleWorkOrder = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowWorkOrderModal(true);
  };

  const handleDelete = async (vehicle) => {
    const confirmed = await ConfirmDialog({
      title: "Delete Vehicle",
      description: `Are you sure you want to delete <b>${
        vehicle.fleetNumber
      }</b>?`,
    });

    if (confirmed) {
      deleteVehicleMutation.mutate(vehicle.vehicle_id);
    }
  };

  const handleNewVehicleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewVehicle({
      ...newVehicle,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const resetNewVehicle = () => {
    setNewVehicle({
      fleetNumber: "",
      warehouse: "",
      licensePlate: "",
      vin: "",
      make: "",
      model: "",
      year: new Date().getFullYear(),
      color: "",
      fuelType: "diesel",
      fuelCapacity: 0,
      odometer: 0,
      engineHours: 0,
      capacityWeight: 0,
      capacityVolume: 0,
      purchaseDate: "",
      purchasePrice: 0,
      gpsDeviceId: "",
      status: "available",
      isActive: true,
      notes: "",
    });
  };

  const handleCreateVehicle = (e) => {
    e.preventDefault();
    createVehicleMutation.mutate(newVehicle);
  };

  const handleEditVehicleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditVehicle({
      ...editVehicle,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleUpdateVehicle = (e) => {
    e.preventDefault();
    updateVehicleMutation.mutate({
      vehicleId: editVehicle.vehicle_id,
      vehicleData: editVehicle,
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    const statusMap = {
      available: "view_status_all",
      in_use: "view_status_approved",
      maintenance: "view_status_pending",
      out_of_service: "view_status_reject",
      retired: "view_status_partly",
      reserved: "view_status_pending",
    };
    return statusMap[status] || "";
  };

  const tableHead = () => (
    <thead>
      <tr>
        <th />
        <th>V I N</th>
        <th>Fleet Number</th>
        <th>License Plate</th>
        <th>Make/Model</th>
        <th>Year</th>
        <th>Warehouse</th>
        <th>Odometer (miles)</th>
        <th>Last Location</th>
        <th>Status</th>
        <th>Active</th>
      </tr>
    </thead>
  );

  const displayTableData = (vehicle) => (
    <>
      <td>
        <Dropdown>
          <Dropdown.Toggle
            variant=""
            className="bg-white border-0"
            bsPrefix="print more"
            style={{ height: "3px" }}
          >
            <DotsHorizontalIcon size={18} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            popperConfig={{ strategy: "fixed" }}
            renderOnMount
            className="dropdown-with-icons"
          >
            <Dropdown.Item onClick={() => handleViewDetails(vehicle)}>
              <EyeIcon size={16} className="me-2" />
              View Details
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleEdit(vehicle)}>
              <EditIcon size={16} className="me-2" />
              Edit Vehicle
            </Dropdown.Item>
            <Dropdown.Item>
              <FileListIcon color={"black"} size={16} className="me-2" />
              Documents
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleWorkOrder(vehicle)}>
              <FaWrench size={16} className="me-2" />
              Raise Work Order
            </Dropdown.Item>
            <Dropdown.Item>
              <FaMapPin size={16} className="me-2" />
              View Location
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              className="text-danger"
              onClick={() => handleDelete(vehicle)}
            >
              <TrashIcon size={16} className="me-2" />
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
      <td className="fw-semibold">{vehicle.vin}</td>
      <td className="fw-semibold">{vehicle.fleetNumber}</td>
      <td>{vehicle.licensePlate}</td>
      <td>
        {vehicle.make} {vehicle.model}
      </td>
      <td>{vehicle.year}</td>
      <td>{vehicle.warehouse}</td>
      <td>{vehicle.odometer.toLocaleString()}</td>
      <td>
        <small className="text-muted">
          {vehicle.lastLocation?.address || "No data"}
        </small>
      </td>
      <td>
        <div className={`px-2 ${getStatusClass(vehicle.status)}`}>
          {vehicle.status.replace("_", " ").toUpperCase()}
        </div>
      </td>
      <td>
        <span
          className={`badge ${
            vehicle.isActive ? "bg-success" : "bg-secondary"
          }`}
        >
          {vehicle.isActive ? "Active" : "Inactive"}
        </span>
      </td>
    </>
  );

  return (
    <section className="invoice-wrapped fleet vehicles">
      <main className="cash-book">
        <div className="content">
          <div className="content-main">
            {/* Filter Sidebar */}
            <div className={`filter-area ${showFilter ? "open" : "close"}`}>
              <div className="filter-header">
                <h2>
                  <FilterTwoIcon size={20} />
                  Filters
                </h2>
                <Button
                  variant="white"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  ×
                </Button>
              </div>
              <div className="filter-body">
                <Form onSubmit={(e) => e.preventDefault()}>
                  <div className="body">
                    <Form.Group className="mb-2-5">
                      <Form.Label>Search</Form.Label>
                      <Form.Control
                        name="q"
                        placeholder="Fleet Number, License, VIN, Make, Model"
                        value={filterParams.q}
                        onChange={handleFilterChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={filterParams.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Statuses</option>
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Fuel Type</Form.Label>
                      <Form.Select
                        name="fuelType"
                        value={filterParams.fuelType}
                        onChange={handleFilterChange}
                        style={{ width: "unset" }}
                      >
                        <option value="">All Fuel Types</option>
                        {fuelTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Active Status</Form.Label>
                      <Form.Select
                        name="isActive"
                        value={filterParams.isActive}
                        onChange={handleFilterChange}
                      >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Form.Select>
                    </Form.Group>

                    <hr className="mt-3 mb-4" />

                    <div className="mb-2-5">
                      <Form.Check
                        inline
                        label="Enable Date Range"
                        name="enableDateRange"
                        type="checkbox"
                        id="enableDateRange"
                        checked={filterParams.enableDateRange}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Purchase Date From</Form.Label>
                      <Form.Control
                        type="date"
                        name="startDate"
                        value={filterParams.startDate}
                        onChange={handleFilterChange}
                        disabled={!filterParams.enableDateRange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-2-5">
                      <Form.Label>Purchase Date To</Form.Label>
                      <Form.Control
                        type="date"
                        name="endDate"
                        value={filterParams.endDate}
                        onChange={handleFilterChange}
                        disabled={!filterParams.enableDateRange}
                      />
                    </Form.Group>
                  </div>

                  <div className="buttons rounded">
                    <Button onClick={resetFilters} variant="white">
                      Reset
                    </Button>
                    <Button onClick={handleSearch} variant="primary">
                      Search
                    </Button>
                  </div>
                </Form>
              </div>
            </div>

            {/* Main Content */}
            <div className="content-body">
              <header className="text-nowrap">
                <h1>
                  {!showFilter && (
                    <button
                      onClick={() => setShowFilter(!showFilter)}
                      className="btn filter"
                    >
                      <FilterTwoIcon size={20} />
                    </button>
                  )}
                  Fleet Vehicles
                  <button
                    title="Refresh"
                    onClick={() => refetch()}
                    className="btn text-primary mx-3"
                  >
                    <CachedIcon />
                  </button>
                </h1>
                <div className="actions d-flex align-items-baseline gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                  >
                    <AddCircleIcon className="me-1" />
                    Add Vehicle
                  </Button>
                  <div className="btn bg print">
                    Export
                    <ExportIcon className="ms-2" />
                  </div>
                </div>
              </header>

              <div className="px-md-4">
                <TableComponent
                  responsive
                  borderless
                  striped
                  className="product-table text-nowrap"
                  tableHeadsFunction={tableHead}
                  tableDataRowFunction={displayTableData}
                  mainDataArray={data.vehicles}
                />
              </div>

              {data.vehicles.length === 0 && (
                <div className="d-flex justify-content-center">
                  <div className="info text-center">
                    <h2 className="my-2">No Vehicles Available</h2>
                  </div>
                </div>
              )}

              {/* Pagination */}
              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={(e) =>
                      setQueryParams({
                        ...queryParams,
                        limit: e.target.value,
                        page: 1,
                      })
                    }
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>
                <p className="m-0">
                  Page {data.page} of {Math.ceil(data.count / data.limit)} (
                  {data.count} total)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Vehicle Modal */}
        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Add New Vehicle</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreateVehicle}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Fleet Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="fleetNumber"
                      value={newVehicle.fleetNumber}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      License Plate <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="licensePlate"
                      value={newVehicle.licensePlate}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      VIN <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Vehicle Identification Number"
                      name="vin"
                      value={newVehicle.vin}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Warehouse <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="warehouse"
                      value={newVehicle.warehouse}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Make <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="make"
                      value={newVehicle.make}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Model <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="model"
                      value={newVehicle.model}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Year <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      name="year"
                      value={newVehicle.year}
                      onChange={handleNewVehicleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Control
                      type="text"
                      name="color"
                      value={newVehicle.color}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Fuel Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="fuelType"
                      value={newVehicle.fuelType}
                      onChange={handleNewVehicleChange}
                      required
                    >
                      {fuelTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fuel Capacity (L)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="fuelCapacity"
                      value={newVehicle.fuelCapacity}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GPS Device ID</Form.Label>
                    <Form.Control
                      type="text"
                      name="gpsDeviceId"
                      value={newVehicle.gpsDeviceId}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Capacity Weight (kg)</Form.Label>
                    <Form.Control
                      type="number"
                      name="capacityWeight"
                      value={newVehicle.capacityWeight}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Capacity Volume (m³)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="capacityVolume"
                      value={newVehicle.capacityVolume}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Purchase Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="purchaseDate"
                      value={newVehicle.purchaseDate}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Purchase Price </Form.Label>
                    <Form.Control
                      type="number"
                      name="purchasePrice"
                      value={newVehicle.purchasePrice}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={newVehicle.status}
                      onChange={handleNewVehicleChange}
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Active</Form.Label>
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      checked={newVehicle.isActive}
                      onChange={handleNewVehicleChange}
                      label="Vehicle is active"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={newVehicle.notes}
                      onChange={handleNewVehicleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={createVehicleMutation.isLoading}
              >
                {createVehicleMutation.isLoading
                  ? "Creating..."
                  : "Create Vehicle"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Vehicle Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Vehicle</Modal.Title>
          </Modal.Header>
          {editVehicle && (
            <Form onSubmit={handleUpdateVehicle}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Fleet Number <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="fleetNumber"
                        value={editVehicle.fleetNumber || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        License Plate <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="licensePlate"
                        value={editVehicle.licensePlate || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        VIN <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Vehicle Identification Number"
                        name="vin"
                        value={editVehicle.vin || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Warehouse <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="warehouse"
                        value={editVehicle.warehouse || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Make <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="make"
                        value={editVehicle.make || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Model <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="model"
                        value={editVehicle.model || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Year <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="year"
                        value={editVehicle.year || ""}
                        onChange={handleEditVehicleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Color</Form.Label>
                      <Form.Control
                        type="text"
                        name="color"
                        value={editVehicle.color || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Fuel Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="fuelType"
                        value={editVehicle.fuelType || "diesel"}
                        onChange={handleEditVehicleChange}
                        required
                      >
                        {fuelTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fuel Capacity (L)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="fuelCapacity"
                        value={editVehicle.fuelCapacity || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>GPS Device ID</Form.Label>
                      <Form.Control
                        type="text"
                        name="gpsDeviceId"
                        value={editVehicle.gpsDeviceId || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Capacity Weight (kg)</Form.Label>
                      <Form.Control
                        type="number"
                        name="capacityWeight"
                        value={editVehicle.capacityWeight || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Capacity Volume (m³)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="capacityVolume"
                        value={editVehicle.capacityVolume || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="purchaseDate"
                        value={editVehicle.purchaseDate ? editVehicle.purchaseDate.split('T')[0] : ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Price </Form.Label>
                      <Form.Control
                        type="number"
                        name="purchasePrice"
                        value={editVehicle.purchasePrice || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={editVehicle.status || "available"}
                        onChange={handleEditVehicleChange}
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Active</Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        checked={editVehicle.isActive || false}
                        onChange={handleEditVehicleChange}
                        label="Vehicle is active"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={editVehicle.notes || ""}
                        onChange={handleEditVehicleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={updateVehicleMutation.isLoading}
                >
                  {updateVehicleMutation.isLoading
                    ? "Updating..."
                    : "Update Vehicle"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Modal>

        {/* Work Order Modal */}
        <Modal
          show={showWorkOrderModal}
          onHide={() => setShowWorkOrderModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Raise Work Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedVehicle && (
              <Form onSubmit={(e) => e.preventDefault()}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle</Form.Label>
                  <Form.Control
                    type="text"
                    value={`${selectedVehicle.fleetNumber} - ${
                      selectedVehicle.make
                    } ${selectedVehicle.model}`}
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Work Order Type</Form.Label>
                  <Form.Select>
                    <option value="">Select Type</option>
                    <option value="scheduled">Scheduled Maintenance</option>
                    <option value="repair">Repair</option>
                    <option value="inspection">Inspection</option>
                    <option value="emergency">Emergency</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describe the work needed..."
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Scheduled Date</Form.Label>
                  <Form.Control type="date" />
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowWorkOrderModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary">Create Work Order</Button>
          </Modal.Footer>
        </Modal>
      </main>
      <ModalLoader show={isLoading || isMainLoading} />
      <VehicleDetailsModal
        show={showDetailsModal}
        vehicle={vehicleDetails?.vehicle}
        backendUrl={backendUrl}
        onHide={() => setShowDetailsModal(false)}
        refetch={refetchDetails}
      />
    </section>
  );
}
